import { useState } from 'react';
import './App.css';

const PLATFORM_COLORS = {
  "Meta / Facebook":  { bg:"rgba(24,119,242,0.15)",  color:"#60a5fa", border:"rgba(96,165,250,0.3)" },
  "Instagram":        { bg:"rgba(225,48,108,0.15)",   color:"#f472b6", border:"rgba(244,114,182,0.3)" },
  "Google Search":    { bg:"rgba(66,133,244,0.15)",   color:"#818cf8", border:"rgba(129,140,248,0.3)" },
  "Google Display":   { bg:"rgba(52,168,83,0.15)",    color:"#34d399", border:"rgba(52,211,153,0.3)" },
  "TikTok":           { bg:"rgba(255,255,255,0.06)",  color:"#cbd5e1", border:"rgba(203,213,225,0.2)" },
  "LinkedIn":         { bg:"rgba(0,119,181,0.15)",    color:"#38bdf8", border:"rgba(56,189,248,0.3)" },
  "YouTube":          { bg:"rgba(255,60,60,0.15)",    color:"#f87171", border:"rgba(248,113,113,0.3)" },
  "Pinterest":        { bg:"rgba(230,0,35,0.15)",     color:"#fb7185", border:"rgba(251,113,133,0.3)" },
};
const DEF_COLOR = { bg:"rgba(139,92,246,0.15)", color:"#a78bfa", border:"rgba(167,139,250,0.3)" };

const INDUSTRIES = ["Health & Fitness","Food & Beverage","E-commerce / Retail","Beauty & Wellness","Home Services","Professional Services","Education & Coaching","Technology / SaaS","Real Estate","Travel & Hospitality","Other"];
const BUDGETS    = ["Under $500/mo","$500 – $2,000/mo","$2,000 – $5,000/mo","$5,000+/mo"];
const GOALS      = ["Drive foot traffic / bookings","Grow e-commerce sales","Generate leads","Build brand awareness","Grow social following"];
const STEPS      = ["Building customer profile","Matching ad platforms","Identifying targeting segments","Scanning competitor gaps","Compiling your report"];

const SECTIONS = [
  { key:"customerProfile",   label:"Ideal Customer Profile",   accent:"#8b5cf6", dim:"rgba(139,92,246,0.1)",  num:"01" },
  { key:"platformMatch",     label:"Platform Recommendations", accent:"#ec4899", dim:"rgba(236,72,153,0.1)",  num:"02" },
  { key:"targetingSegments", label:"Targeting & Keywords",     accent:"#06b6d4", dim:"rgba(6,182,212,0.1)",   num:"03" },
  { key:"competitorGaps",    label:"Competitor Audience Gaps", accent:"#f59e0b", dim:"rgba(245,158,11,0.1)",  num:"04" },
  { key:"quickWins",         label:"30-Day Action Plan",       accent:"#10b981", dim:"rgba(16,185,129,0.1)",  num:"05" },
];

export default function App() {
  const [bizName,  setBizName]  = useState('');
  const [industry, setIndustry] = useState('');
  const [bizDesc,  setBizDesc]  = useState('');
  const [budget,   setBudget]   = useState('');
  const [goal,     setGoal]     = useState('');
  const [loading,  setLoading]  = useState(false);
  const [step,     setStep]     = useState(-1);
  const [report,   setReport]   = useState(null);
  const [error,    setError]    = useState('');

  async function generate() {
    if (!bizName.trim() || !industry || !bizDesc.trim()) {
      setError('Please fill in Business Name, Industry, and Description.'); return;
    }
    setError(''); setLoading(true); setReport(null); setStep(0);
    STEPS.forEach((_, i) => setTimeout(() => setStep(i), i * 1000));
    try {
      const res  = await fetch('/api/report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bizName, industry, bizDesc, budget, goal }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Something went wrong');
      setReport(data);
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setLoading(false); setStep(-1);
    }
  }

  function reset() {
    setReport(null); setBizName(''); setIndustry('');
    setBizDesc(''); setBudget(''); setGoal(''); setError('');
  }

  return (
    <div className="app">
      <div className="orb orb-1" />
      <div className="orb orb-2" />
      <div className="orb orb-3" />
      <div className="noise" />

      <div className="shell">

        {/* NAV */}
        <nav className="nav">
          <div className="nav-logo">
            <div className="nav-icon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"
                  stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <span>SmartAudience</span>
          </div>
          <span className="nav-tag">Beta</span>
        </nav>

        {/* LOADER */}
        {loading && (
          <div className="loader">
            <div className="loader-ring"><div className="loader-ring-inner" /></div>
            <div className="loader-title">Analysing your audience</div>
            <div className="loader-steps">
              {STEPS.map((s, i) => (
                <div key={i} className={`loader-step${i < step ? ' done' : i === step ? ' active' : ''}`}>
                  <span className="step-dot">{i < step ? '✓' : i === step ? '›' : '·'}</span>
                  {s}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* FORM */}
        {!loading && !report && (
          <div className="form-wrap">
            <div className="hero">
              <div className="hero-tag">AI Audience Intelligence</div>
              <h1>Know exactly who to<br/><em>target</em> — and where.</h1>
              <p>Describe your business and get a complete audience brief in under 30 seconds.<br/>No marketing experience needed.</p>
            </div>

            <div className="card">
              <div className="card-head">
                <span className="card-head-label">Your business details</span>
                <span className="card-head-hint">Takes about 60 seconds</span>
              </div>

              <div className="two-col">
                <div className="field">
                  <label>Business name</label>
                  <input value={bizName} onChange={e=>setBizName(e.target.value)} placeholder="e.g. Luna Yoga Studio" />
                </div>
                <div className="field">
                  <label>Industry</label>
                  <select value={industry} onChange={e=>setIndustry(e.target.value)}>
                    <option value="">Select industry…</option>
                    {INDUSTRIES.map(v=><option key={v}>{v}</option>)}
                  </select>
                </div>
              </div>

              <div className="field" style={{marginBottom:18}}>
                <label>Describe your business & ideal customers</label>
                <textarea value={bizDesc} onChange={e=>setBizDesc(e.target.value)}
                  placeholder="e.g. We run a boutique yoga studio targeting stressed professionals aged 28–45 who want to unwind after work. We offer morning and evening classes plus weekend retreats…" />
              </div>

              <div className="two-col">
                <div className="field">
                  <label>Monthly ad budget</label>
                  <select value={budget} onChange={e=>setBudget(e.target.value)}>
                    <option value="">Select budget…</option>
                    {BUDGETS.map(v=><option key={v}>{v}</option>)}
                  </select>
                </div>
                <div className="field">
                  <label>Primary goal</label>
                  <select value={goal} onChange={e=>setGoal(e.target.value)}>
                    <option value="">Select goal…</option>
                    {GOALS.map(v=><option key={v}>{v}</option>)}
                  </select>
                </div>
              </div>

              {error && <div className="error-box">{error}</div>}

              <button className="cta" onClick={generate}>
                <span>⚡</span>
                Generate My Audience Intelligence Report
                <span className="cta-arrow">→</span>
              </button>

              <div className="cta-note">Powered by Claude AI · Reports ready in under 30 seconds</div>
            </div>
          </div>
        )}

        {/* RESULTS */}
        {!loading && report && (
          <div className="results-wrap">
            <div className="results-top">
              <div>
                <div className="results-eyebrow">Audience Intelligence Report</div>
                <div className="results-biz">{bizName}</div>
              </div>
              <button className="ghost-btn" onClick={reset}>← New report</button>
            </div>

            <div className="sections">
              {SECTIONS.map(({ key, label, accent, dim, num }) => {
                const d = report[key];
                if (!d) return null;
                return (
                  <div key={key} className="sec" style={{'--a': accent, '--d': dim}}>
                    <div className="sec-head">
                      <span className="sec-num">{num}</span>
                      <span className="sec-label">{label}</span>
                    </div>

                    {(key === 'customerProfile' || key === 'competitorGaps') && (
                      <p className="sec-body">{d}</p>
                    )}

                    {key === 'platformMatch' && (
                      <>
                        <p className="sec-body">{d.reasoning}</p>
                        <div className="chips">
                          {(d.recommended||[]).map(p => {
                            const c = PLATFORM_COLORS[p] || DEF_COLOR;
                            return <span key={p} className="chip-platform"
                              style={{background:c.bg, color:c.color, borderColor:c.border}}>{p}</span>;
                          })}
                        </div>
                        {d.avoid?.length > 0 && (
                          <div className="avoid">
                            <span className="avoid-lbl">Skip</span>
                            {d.avoid.join(', ')} — {d.avoidReason}
                          </div>
                        )}
                      </>
                    )}

                    {key === 'targetingSegments' && (
                      <>
                        {d.interests?.length > 0 && (
                          <div className="chip-group">
                            <div className="chip-group-label">Interests</div>
                            <div className="chips">{d.interests.map(t=><span key={t} className="chip c-purple">{t}</span>)}</div>
                          </div>
                        )}
                        {d.behaviours?.length > 0 && (
                          <div className="chip-group">
                            <div className="chip-group-label">Behaviours</div>
                            <div className="chips">{d.behaviours.map(t=><span key={t} className="chip c-pink">{t}</span>)}</div>
                          </div>
                        )}
                        {d.keywords?.length > 0 && (
                          <div className="chip-group">
                            <div className="chip-group-label">Search keywords</div>
                            <div className="chips">{d.keywords.map(t=><span key={t} className="chip c-cyan">{t}</span>)}</div>
                          </div>
                        )}
                        {d.lookalike && (
                          <div className="lookalike">
                            <div className="lookalike-lbl">Lookalike audience seed</div>
                            <div className="lookalike-body">{d.lookalike}</div>
                          </div>
                        )}
                      </>
                    )}

                    {key === 'quickWins' && (
                      <div className="qw-list">
                        {typeof d === 'string'
                          ? d.split('\n').filter(Boolean).map((line, i) => (
                              <div key={i} className="qw-item">
                                <span className="qw-n">{i+1}</span>
                                <span className="qw-text">{line.replace(/^\d+\.\s*/,'')}</span>
                              </div>
                            ))
                          : <p className="sec-body">{d}</p>
                        }
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <button className="ghost-btn-lg" onClick={reset}>← Analyse a different business</button>
          </div>
        )}

      </div>
    </div>
  );
}
